import type {
  SlotName,
  ThemeDefinition,
  ThemeId,
  ThemeSlots,
} from "./types";

import { defaultTheme } from "./default";
import { b2bTheme } from "./b2b";
import { b2cTheme } from "./b2c";
import { elevenlabsTheme } from "./elevenlabs";

const DEFAULT_THEME_ID: ThemeId = "default";

/**
 * Central registry of all themes. Adding a theme = adding a folder under
 * src/themes/<id>/ and a line to this map. Nothing else needs to change.
 */
const themes: ReadonlyMap<ThemeId, ThemeDefinition> = new Map<
  ThemeId,
  ThemeDefinition
>([
  [defaultTheme.manifest.id, defaultTheme],
  [b2bTheme.manifest.id, b2bTheme],
  [b2cTheme.manifest.id, b2cTheme],
  [elevenlabsTheme.manifest.id, elevenlabsTheme],
]);

/** Returns the default theme. Used as the ultimate fallback. */
export const getDefaultTheme = (): ThemeDefinition => {
  const theme = themes.get(DEFAULT_THEME_ID);
  if (!theme) {
    throw new Error(
      `Theme registry is missing the required '${DEFAULT_THEME_ID}' theme.`
    );
  }
  return theme;
};

/**
 * Looks up a theme by id. Returns the default theme when the id is unknown
 * or empty — never throws. This matches the existing ThemeContext behaviour
 * where unknown values silently fall back to default.
 */
export const resolveTheme = (
  id: ThemeId | null | undefined
): ThemeDefinition => {
  if (!id) return getDefaultTheme();
  return themes.get(id) ?? getDefaultTheme();
};

/** Returns true when the id refers to a registered theme. */
export const isKnownTheme = (id: ThemeId | null | undefined): boolean =>
  !!id && themes.has(id);

/** Returns every registered theme. Used by the admin UI dropdown. */
export const listThemes = (): ReadonlyArray<ThemeDefinition> =>
  Array.from(themes.values());

/**
 * Walks the `extends` chain to find the first theme that defines a slot.
 * Returns `undefined` when no theme in the chain defines the slot —
 * the caller (useThemeSlot) then falls back to the built-in implementation.
 *
 * The walk terminates at `extends: null`. A depth guard prevents infinite
 * loops if a manifest accidentally creates a cycle.
 */
export const resolveSlot = <K extends SlotName>(
  themeId: ThemeId,
  slot: K
): ThemeSlots[K] | undefined => {
  const MAX_DEPTH = 10;
  let current: ThemeDefinition | undefined = themes.get(themeId);
  let depth = 0;

  while (current && depth < MAX_DEPTH) {
    const candidate = current.slots[slot];
    if (candidate) return candidate;

    const parentId = current.manifest.extends;
    if (!parentId) return undefined;
    current = themes.get(parentId);
    depth++;
  }

  return undefined;
};
